import {
  BaseTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./BaseTable";
// needed for table body level scope DnD setup
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import React, { useEffect, useState } from "react";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { cn, getCommonPinningStyles } from "@/utils/cn";
import {
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import BasePagination from "./BasePagination";
import BaseTableSearchFilter from "./BaseTableSearchFilter";
import { CSS } from "@dnd-kit/utilities";
import DataTableColumnHeader from "./DataTableColumnHeader";
import RenderToast from "../RenderToast";
import { baseSortColumn } from "./BaseSortColumn";
import cx from "clsx";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import styles from "./index.module.css";
import { usePut } from "@/apiHooks/HttpFunctions";
// needed for row & cell level scope DnD setup
import { useSortable } from "@dnd-kit/sortable";

/**
 * DataTable Component
 * @param {Array} columns - The columns configuration for the table
 * @param {Object} data - The data to be displayed (should include pagination details)
 * @param {boolean} isLoading - Indicates if data is currently loading
 * @param {string} searchKey - Key used for search functionality
 * @param {{ api: string, label: string }} exportData - Data export configuration
 * @param {{ api: string, label: string }} exportZipData - Data export zip configuration
 * @param {{ label: string, onClick: Function }} addData - Add new data configuration
 * @param {{ onClick: Function }} importData - Import data configuration
 * @param {Array} filterData - Array of filters for the table
 */

// Row Component
export const DraggableRow = ({ row, idx, table }) => {
  const { transform, transition, setNodeRef, isDragging } = useSortable({
    id: row.original.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform), //let dnd-kit do its thing
    transition: transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1 : 0,
    position: "relative",
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      key={row.id}
      className={cx(
        "h-8 py-1 cursor-pointer hover:bg-[#EEEEEE] transition-all duration-100",
        row.getIsSelected() &&
          "bg-gradient-to-b from-[#456565] to-[#50A1A1] text-white"
      )}
      onClick={() => row.toggleSelected()}
    >
      {row.getVisibleCells().map((cell, cIdx) => (
        <TableCell
          key={cell.id}
          style={{
            minWidth: cell.column.getSize(),
            ...getCommonPinningStyles(cell.column, row),
          }}
          className={cx(
            "px-2 py-0 whitespace-nowrap border-[#456565] border-e",
            cIdx === 0 ? "border-s" : "border-s-0",
            idx === table.getRowModel().rows.length - 1 && "border-b"
          )}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
      ))}
    </TableRow>
  );
};

function DataTable({
  columns,
  data,
  isLoading = false,
  searchKey,
  exportData,
  exportZipData,
  addData,
  sortData,
  editTableData,
  importData,
  filterData,
  isFullPageTable = true,
  customHeaderActionsData = [],
  enablePagination = true,
  height = "h-full",
}) {
  // States for table
  const [rowSelection, setRowSelection] = React.useState({});
  const [columnSizing, setColumnSizing] = React.useState({});
  const [sorting, setSorting] = React.useState();

  const handleSuccess = (text, { oldIndex, newIndex }) => {
    setTempData((data) => {
      const updatedData = arrayMove(data, oldIndex, newIndex);
      return updatedData;
    });
    setOriginalData(tempData);
    RenderToast({
      type: "success",
      message: text,
    });
  };

  const handleError = (error) => {
    setTempData([...originalData]);
    RenderToast({ type: "error", message: error.response.data.errors });
  };

  const defaultCellRenderer = ({ cell }) => {
    const value = cell.value;
    return value !== null && value !== undefined ? value : "-";
  };
  const wrapWithDefaultRenderer = (columns) => {
    const newColumns = columns.map((col) => {
      const isComponentCell =
        typeof col.cell === "function" || React.isValidElement(col.cell);

      if (isComponentCell) {
        return col;
      }

      return {
        ...col,
        cell: (cellContext) => {
          const value = cellContext.cell.getValue();
          return defaultCellRenderer({ cell: { ...cellContext.cell, value } });
        },
      };
    });

    if (sortData) {
      return [...baseSortColumn(), ...newColumns];
    } else {
      return newColumns;
    }
  };

  const [tempData, setTempData] = useState(data?.data ?? []);
  const [originalData, setOriginalData] = useState(data?.data ?? []);

  useEffect(() => {
    setTempData(data?.data ?? []);
  }, [data]);

  const dataIds = React.useMemo(
    () => tempData?.map(({ id }) => id),
    [tempData]
  );

  const { mutate: updateSort } = usePut({
    apiURL: `${sortData?.api}/sort`,
    isFormData: false,
    onSuccess: (response, { oldIndex, newIndex }) => {
      handleSuccess(response.message, { oldIndex, newIndex });
    },
    onError: (error) => {
      handleError(error);
    },
  });

  // React Table Instance
  const table = useReactTable({
    data: tempData || [],
    columns: wrapWithDefaultRenderer(columns),
    defaultColumn: {
      enableResizing: true,
      enableSorting: true,
      size: 20,
    },
    state: {
      rowSelection,
      columnSizing,
      sorting,
    },
    initialState: {
      columnPinning: {
        left: ["drag-handle", "actions"],
      },
    },
    manualPagination: true,
    pageCount: data?.last_page || 0,
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onColumnSizingChange: setColumnSizing,
    onSortingChange: setSorting,
    columnResizeMode: "onChange",
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  // reorder rows after drag & drop
  function handleDragEnd(event) {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      const oldIndex = dataIds.indexOf(active.id);
      const newIndex = dataIds.indexOf(over.id);

      setOriginalData([...tempData]);

      // Handle call sort api
      updateSort({
        from_id: active.id,
        to_id: over.id,
        oldIndex,
        newIndex,
      });
    }
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  return (
    <DndContext
      key={tempData.map(({ id }) => id).join("-")}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <div
        className={cn(
          isFullPageTable ? height : "",
          "bg-white overflow-hidden flex flex-col gap-y-4"
        )}
      >
        {/* Table Filters and Controls */}
        <BaseTableSearchFilter
          searchKey={searchKey}
          exportData={exportData}
          exportZipData={exportZipData}
          addData={addData}
          sortData={sortData}
          editTableData={editTableData}
          importData={importData}
          filterData={filterData}
          data={data}
          customHeaderActionsData={customHeaderActionsData}
        />

        {/* Table */}
        <div className={`max-${height} overflow-auto`}>
          <BaseTable
            className="border-separate border-spacing-0"
            style={{ width: table.getTotalSize() }}
          >
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow
                  key={headerGroup.id}
                  className="sticky z-10 top-0 bg-white cursor-default"
                >
                  {headerGroup.headers.map((header, idx) => (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      className={cx(
                        "h-12 text-sm relative font-normal text-center bg-white border-e border-t border-b !border-[#456565]",
                        idx === 0 ? "border-s" : "border-s-0",
                        styles["resizer-hover"]
                      )}
                      style={{
                        ...getCommonPinningStyles(header.column),
                        width: header.getSize(),
                      }}
                    >
                      {header.isPlaceholder ? null : (
                        <DataTableColumnHeader
                          className={cn(
                            "text-center",
                            header.column.columnDef.headerColor
                          )}
                          column={header.column}
                          title={header.column.columnDef.header}
                        />
                      )}

                      {header.column.getCanResize() && (
                        <div
                          {...{
                            onMouseDown: header.getResizeHandler(),
                            onTouchStart: header.getResizeHandler(),
                            className: cx(
                              styles["resizer"],
                              header.column.getIsResizing() &&
                                styles["isResizing"]
                            ),
                          }}
                        />
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody className="bg-white text-sm font-light relative">
              <SortableContext
                key={`${Date.now()}`}
                items={dataIds}
                strategy={verticalListSortingStrategy}
              >
                {isLoading && (
                  <TableRow
                    className={cn(
                      table.getRowModel().rows.length
                        ? "absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-sm z-10"
                        : null
                    )}
                  >
                    <TableCell
                      colSpan={columns.length + (sortData ? 1 : 0)}
                      className={cn(
                        table.getRowModel().rows.length
                          ? null
                          : "h-24 text-center border !border-[#456565]"
                      )}
                    >
                      Loading data...
                    </TableCell>
                  </TableRow>
                )}

                {/* Existing Data */}
                {table.getRowModel().rows.length ? (
                  table
                    .getRowModel()
                    .rows.map((row, idx) => (
                      <DraggableRow
                        key={row.id}
                        row={row}
                        idx={idx}
                        table={table}
                      />
                    ))
                ) : !isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + (sortData ? 1 : 0)}
                      className="h-24 text-center border !border-[#456565]"
                    >
                      No results.
                    </TableCell>
                  </TableRow>
                ) : null}
              </SortableContext>
            </TableBody>
          </BaseTable>
        </div>

        {/* Divider */}
        <div className="relative mt-2 mb-1 w-full">
          <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-[#9A8938] via-[#E1D05E] to-[#BB9A33]" />
        </div>

        {/* Pagination */}
        {enablePagination && <BasePagination data={data} />}
      </div>
    </DndContext>
  );
}

export default DataTable;
